import { API_BASE_URL } from '../config';
import { API_ENDPOINTS, HTTP_METHODS } from '../constants/api';
import tokenService from './tokenService';

// Track ongoing requests to prevent duplicates
const ongoingRequests = new Map();

// Clean up old requests periodically to prevent memory leaks
setInterval(() => {
  const now = Date.now();
  for (const [key, requestData] of ongoingRequests.entries()) {
    // If a request has been pending for more than 30 seconds, remove it
    if (now - requestData.timestamp > 30000) {
      ongoingRequests.delete(key);
    }
  }
}, 60000); // Check every minute

// Generate a unique key for each request
const getRequestKey = (method, url, data) => {
  const dataString = data ? JSON.stringify(data) : '';
  return `${method}:${url}:${dataString}`;
};

// CSRF double-submit token for cookie-authenticated /api/auth requests.
// Fetched lazily, cached, refreshed once on a CSRF_ERROR response.
let csrfToken = null;
const fetchCsrfToken = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/csrf`, { credentials: 'include' });
  const json = await response.json();
  csrfToken = json?.data?.csrfToken || null;
  return csrfToken;
};
const needsCsrf = (method, url) =>
  method !== 'GET' && method !== 'HEAD' && url.includes('/api/auth');

// API client with automatic token refresh and request deduplication
const apiClient = {
  request: async (method, url, data = null, options = {}) => {
    const requestKey = getRequestKey(method, url, data);
    
    // If the same request is already in progress, return the existing promise
    if (ongoingRequests.has(requestKey)) {
      console.log('Deduplicating request:', requestKey);
      return ongoingRequests.get(requestKey).promise;
    }

    const config = {
      method,
      // Send the httpOnly refresh_token cookie (scoped to /api/auth) with requests.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    // Add authorization header if token exists
    const accessToken = tokenService.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (data) {
      config.body = JSON.stringify(data);
    }

    // Create the request promise
    const requestPromise = (async () => {
      try {
        if (needsCsrf(method, url)) {
          if (!csrfToken) await fetchCsrfToken();
          config.headers['X-CSRF-Token'] = csrfToken;
        }

        let response = await fetch(url, config);
        let responseData = await response.json();

        // Stale/missing CSRF token: fetch a fresh one and retry once.
        if (response.status === 403 && responseData.code === 'CSRF_ERROR') {
          await fetchCsrfToken();
          config.headers['X-CSRF-Token'] = csrfToken;
          response = await fetch(url, config);
          responseData = await response.json();
        }

        // If token expired, try to refresh
        if (response.status === 401 && responseData.code === 'TOKEN_EXPIRED') {
          try {
            const newAccessToken = await tokenService.refreshAccessToken();
            
            // Retry the original request with new token
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            const retryResponse = await fetch(url, config);
            return await retryResponse.json();
          } catch (refreshError) {
            // Refresh failed, redirect to login
            tokenService.clearTokens();
            window.location.href = '/login';
            throw refreshError;
          }
        }

        return responseData;
      } catch (error) {
        console.error('API request failed:', error);
        throw error;
      } finally {
        // Remove the request from ongoing requests
        ongoingRequests.delete(requestKey);
      }
    })();

    // Store the request promise with timestamp
    ongoingRequests.set(requestKey, {
      promise: requestPromise,
      timestamp: Date.now()
    });
    
    return requestPromise;
  }
};

// Generic API request function
const apiRequest = async (method, endpoint, data = null, config = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Add query parameters for GET requests
    if (method === 'GET' && config.params) {
      const params = new URLSearchParams(config.params);
      const urlWithParams = `${url}?${params.toString()}`;
      const response = await apiClient.request(method, urlWithParams, null, config);
      return response;
    }

    const response = await apiClient.request(method, url, data, config);
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    return { success: false, data: null, error: error.message };
  }
};

// Links API
export const linksAPI = {
  // Create a new link
  create: (linkData) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.LINKS, linkData),
  
  // Get all links
  getAll: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.LINKS),
  
  // Get link by short code
  getByShortCode: (shortCode) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}`),
  
  // Delete link
  delete: (shortCode) => apiRequest(HTTP_METHODS.DELETE, `${API_ENDPOINTS.LINKS}/${shortCode}`),
  
  // Get analytics for a link
  getAnalytics: (shortCode) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics`),
  
  // Get detailed analytics for a link
  getDetailedAnalytics: (shortCode, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics/detailed`, null, { params }),
  
  // Get geographic analytics for a link
  getGeographicAnalytics: (shortCode, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics/geographic`, null, { params }),
  
  // Get country analytics for a link
  getCountryAnalytics: (shortCode, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics/countries`, null, { params }),
  
  // Get city analytics for a link
  getCityAnalytics: (shortCode, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics/cities`, null, { params }),

  // Clicks over time (human vs bot)
  getTimeseries: (shortCode, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics/timeseries`, null, { params }),

  // Browser + OS breakdown
  getTechBreakdown: (shortCode) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/analytics/tech`),

  // CSV export URL for a link's raw clicks (open in a new tab / download)
  analyticsCsvUrl: (shortCode) => `${API_BASE_URL}${API_ENDPOINTS.LINKS}/${shortCode}/analytics/export`,

  // Email the current user an analytics summary
  emailReport: (days = 7) => apiRequest(HTTP_METHODS.POST, `${API_ENDPOINTS.LINKS}/report/email`, null, { params: { days } }),
  
  // Social Media Sharing
  getSocialShareUrls: (shortCode, title = '', description = '') => {
    const params = new URLSearchParams({ title, description });
    return apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/share?${params}`);
  },
  
  trackSocialShare: (shortCode, platform) => {
    return apiRequest(HTTP_METHODS.POST, `${API_ENDPOINTS.LINKS}/${shortCode}/share`, { platform });
  },
  
  getSocialShareStats: (shortCode) => {
    return apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.LINKS}/${shortCode}/share/stats`);
  },
};

// Profile Pages API
export const profilePagesAPI = {
  // Create a new profile page
  create: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.PROFILE_PAGES, data),
  
  // Get all profile pages
  getAll: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.PROFILE_PAGES, null, { params }),
  
  // Get profile page by ID
  getById: (id) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.PROFILE_PAGES}/${id}`),
  
  // Update profile page
  update: (id, data) => apiRequest(HTTP_METHODS.PUT, `${API_ENDPOINTS.PROFILE_PAGES}/${id}`, data),
  
  // Delete profile page
  delete: (id) => apiRequest(HTTP_METHODS.DELETE, `${API_ENDPOINTS.PROFILE_PAGES}/${id}`),
  
  // Add link to profile page
  addLink: (pageId, data) => apiRequest(HTTP_METHODS.POST, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/links`, data),
  
  // Remove link from profile page
  removeLink: (pageId, linkId) => apiRequest(HTTP_METHODS.DELETE, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/links/${linkId}`),
  
  // Update link order in profile page
  updateLinkOrder: (pageId, linkId, data) => apiRequest(HTTP_METHODS.PUT, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/links/${linkId}/order`, data),
  
  // Get available links for a profile page
  getAvailableLinks: (pageId) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/available-links`),
  
  // Get public profile page
  getPublic: (username, pageName) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.PROFILE_PAGES}/public/${username}/${pageName}`),
  
  // Get profile page analytics
  getAnalytics: (pageId, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/analytics`, null, { params }),

  // Update a page-link's thumbnail / scheduling window
  updateLinkAppearance: (pageId, linkId, data) => apiRequest(HTTP_METHODS.PUT, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/links/${linkId}/appearance`, data),

  // Get lead-capture submissions for a page
  getLeads: (pageId) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.PROFILE_PAGES}/${pageId}/leads`),
};

// Analytics API
export const analyticsAPI = {
  // Get analytics data
  getAnalytics: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ANALYTICS, null, { params }),
};

// System API
export const systemAPI = {
  // Health check
  healthCheck: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.HEALTH),
  
  // Get server info
  getServerInfo: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.SERVER_INFO),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboard: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.DASHBOARD),

  // Audit log
  getAuditLogs: (params = {}) => apiRequest(HTTP_METHODS.GET, '/api/admin/audit-logs', null, { params }),
  
  // Users
  getUsers: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.USERS, null, { params }),
  exportUsers: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.USERS + '/export', null, { params }),
  bulkUpdateUsers: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.ADMIN.USERS + '/bulk', data),
  getUserDetails: (id) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.ADMIN.USERS}/${id}`),
  updateUser: (id, data) => apiRequest(HTTP_METHODS.PUT, `${API_ENDPOINTS.ADMIN.USERS}/${id}`, data),
  deleteUser: (id) => apiRequest(HTTP_METHODS.DELETE, `${API_ENDPOINTS.ADMIN.USERS}/${id}`),
  getUserUsage: (id, params = {}) => apiRequest(HTTP_METHODS.GET, `${API_ENDPOINTS.ADMIN.USERS}/${id}/usage`, null, { params }),
  
  // System Health
  getSystemHealth: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.SYSTEM_HEALTH),
  
  // System Analytics
  getSystemAnalytics: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.SYSTEM_ANALYTICS, null, { params }),
  
  // Settings
  getSettings: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.SETTINGS, null, { params }),
  updateSettings: (data) => apiRequest(HTTP_METHODS.PUT, API_ENDPOINTS.ADMIN.SETTINGS, data),
  
  // Rate Limits
  getRateLimitSettings: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.RATE_LIMITS),
  updateRateLimitSettings: (data) => apiRequest(HTTP_METHODS.PUT, API_ENDPOINTS.ADMIN.RATE_LIMITS, data),
  resetRateLimitSettings: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.ADMIN.RATE_LIMITS + '/reset'),
  
  // Export/Import Settings
  exportSettings: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.EXPORT_SETTINGS, null, { params }),
  importSettings: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.ADMIN.IMPORT_SETTINGS, data),

  // Pricing plan management
  getPlans: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.ADMIN.PLANS),
  createPlan: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.ADMIN.PLANS, data),
  updatePlan: (key, data) => apiRequest(HTTP_METHODS.PUT, `${API_ENDPOINTS.ADMIN.PLANS}/${key}`, data),
  deletePlan: (key) => apiRequest(HTTP_METHODS.DELETE, `${API_ENDPOINTS.ADMIN.PLANS}/${key}`),
};

// Webhooks API
export const webhooksAPI = {
  list: () => apiRequest(HTTP_METHODS.GET, '/api/webhooks'),
  create: (data) => apiRequest(HTTP_METHODS.POST, '/api/webhooks', data),
  update: (id, data) => apiRequest(HTTP_METHODS.PUT, `/api/webhooks/${id}`, data),
  remove: (id) => apiRequest(HTTP_METHODS.DELETE, `/api/webhooks/${id}`),
};

// Workspaces API
export const workspacesAPI = {
  list: () => apiRequest(HTTP_METHODS.GET, '/api/workspaces'),
  create: (data) => apiRequest(HTTP_METHODS.POST, '/api/workspaces', data),
  get: (id) => apiRequest(HTTP_METHODS.GET, `/api/workspaces/${id}`),
  rename: (id, name) => apiRequest(HTTP_METHODS.PUT, `/api/workspaces/${id}`, { name }),
  remove: (id) => apiRequest(HTTP_METHODS.DELETE, `/api/workspaces/${id}`),
  invite: (id, data) => apiRequest(HTTP_METHODS.POST, `/api/workspaces/${id}/invite`, data),
  accept: (token) => apiRequest(HTTP_METHODS.POST, '/api/workspaces/accept', { token }),
  removeMember: (id, userId) => apiRequest(HTTP_METHODS.DELETE, `/api/workspaces/${id}/members/${userId}`),
  links: (id) => apiRequest(HTTP_METHODS.GET, `/api/workspaces/${id}/links`),
};

// Billing API (plans catalog, subscription status, checkout, cancel)
export const billingAPI = {
  getPlans: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.BILLING.PLANS),
  getSubscription: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.BILLING.SUBSCRIPTION),
  getInvoices: (params = {}) => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.BILLING.INVOICES, null, { params }),
  checkout: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.BILLING.CHECKOUT, data),
  portal: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.BILLING.PORTAL),
  cancel: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.BILLING.CANCEL),
};

// Auth API
export const authAPI = {
  // Register a new user
  register: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.REGISTER, data),
  
  // Login user
  login: (data) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.LOGIN, data),
  
  // Refresh access token (refresh token travels in the httpOnly cookie)
  refreshToken: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.REFRESH),

  // Logout user (refresh token travels in the httpOnly cookie)
  logout: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.LOGOUT),
  
  // Logout from all devices
  logoutAll: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.LOGOUT_ALL),
  
  // Get user profile
  getProfile: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.AUTH.PROFILE),
  
  // Update user profile
  updateProfile: (data) => apiRequest(HTTP_METHODS.PUT, API_ENDPOINTS.AUTH.PROFILE, data),

  // Request a password reset link
  forgotPassword: (email) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email }),

  // Complete a password reset
  resetPassword: (token, newPassword) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.RESET_PASSWORD, { token, newPassword }),

  // Which third-party sign-in providers are configured (e.g. { google: true })
  getProviders: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.AUTH.PROVIDERS),

  // Full-page URL that starts the Google OAuth flow (navigate, don't fetch).
  googleStartUrl: () => `${API_BASE_URL}${API_ENDPOINTS.AUTH.GOOGLE_START}`,

  // Verify email with token
  verifyEmail: (token) => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.VERIFY_EMAIL, { token }),

  // Resend the verification email
  resendVerification: () => apiRequest(HTTP_METHODS.POST, API_ENDPOINTS.AUTH.RESEND_VERIFICATION),

  // Two-factor (TOTP)
  setup2FA: () => apiRequest(HTTP_METHODS.POST, '/api/auth/2fa/setup'),
  enable2FA: (token) => apiRequest(HTTP_METHODS.POST, '/api/auth/2fa/enable', { token }),
  disable2FA: (token) => apiRequest(HTTP_METHODS.POST, '/api/auth/2fa/disable', { token }),

  // GDPR: download all personal data as a JSON file (returns a Blob)
  exportMyData: async () => {
    const response = await fetch(`${API_BASE_URL}/api/auth/me/export`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${tokenService.getAccessToken()}` }
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },

  // GDPR: permanently delete the account (password + optional TOTP re-verify)
  deleteAccount: (password, totp) => apiRequest(HTTP_METHODS.DELETE, '/api/auth/me', { password, totp }),

  // Get active sessions
  getActiveSessions: () => apiRequest(HTTP_METHODS.GET, API_ENDPOINTS.AUTH.SESSIONS),
  
  // Revoke a specific session
  revokeSession: (sessionId) => apiRequest(HTTP_METHODS.DELETE, `${API_ENDPOINTS.AUTH.REVOKE_SESSION}/${sessionId}`),
};

export { apiRequest };
export default apiClient;
